import React, { memo, useState, useEffect, useCallback } from "react";
import { loadStripe } from "@stripe/stripe-js/pure";
import type { Stripe, StripeElementsOptions } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { createSelector } from "reselect";

import { Loading } from "components";
import { TStore } from "models";
import { useSelector } from "react-redux";
import { rollbar } from "utils";
import { STRIPE_PUBLISHABLE_KEY } from "config_environment";
import { canLoadStripe, isCurrentStripeContext } from "./stripeLoading";

const stateSelectorHandle = createSelector(
	(state: TStore) => state.community,
	(community) => ({
		community,
	}),
);

interface IStripeWrapper {
	children: React.ReactNode;
	paymentsEnabled: boolean;
	stripeLinkedAccountId?: string | null;
	setupIntentClientSecret?: string;
}

const StripeWrapper = ({
	children,
	paymentsEnabled,
	stripeLinkedAccountId,
	setupIntentClientSecret,
}: IStripeWrapper) => {
	const [stripeContext, setStripeContext] = useState<{
		accountId: string;
		stripe: Stripe | null;
	} | null>(null);

	const stateSelector = useCallback(stateSelectorHandle, []);
	const { community } = useSelector(stateSelector);

	useEffect(() => {
		let cancelled = false;
		const stripeCanLoad = canLoadStripe({
			paymentsEnabled,
			stripeLinkedAccountId,
			stripePublishableKey: STRIPE_PUBLISHABLE_KEY,
		});

		if (!stripeCanLoad) {
			setStripeContext(null);
			return () => {
				cancelled = true;
			};
		}

		const fetchStripeObject = async () => {
			try {
				const res = await loadStripe(STRIPE_PUBLISHABLE_KEY, {
					stripeAccount: stripeLinkedAccountId!,
				});
				if (!cancelled) {
					setStripeContext({
						accountId: stripeLinkedAccountId!,
						stripe: res,
					});
				}
			} catch (error) {
				rollbar.error(error);
			}
		};

		fetchStripeObject();
		return () => {
			cancelled = true;
		};
	}, [paymentsEnabled, stripeLinkedAccountId]);

	if (!paymentsEnabled) {
		return null;
	}

	const stripeObject = isCurrentStripeContext({
		paymentsEnabled,
		stripeLinkedAccountId,
		loadedStripeAccountId: stripeContext?.accountId,
	})
		? stripeContext?.stripe
		: null;

	if (!stripeObject) {
		return <Loading />;
	}

	const appearance = {
		theme: "stripe" as "stripe" | "none" | "night" | undefined,
		variables: {
			colorPrimary: community?.data?.primary_color ?? undefined,
			colorBackground: community?.data?.primary_color ?? undefined,
		},
	};

	const options: StripeElementsOptions = {
		clientSecret: setupIntentClientSecret ?? undefined,
		appearance,
	};

	return (
		<Elements stripe={stripeObject} options={options}>
			{children}
		</Elements>
	);
};

export default memo(StripeWrapper);
